import { createSelector } from '@ngrx/store';
import { Country } from '../../entity/locale';
import { AppState } from '../appState';
import { FirmSelectors } from '../firm/firm.selectors';
import { ListSelectors } from '../list/list.selectors';
import {
  AppModule,
  AppModuleType,
  AppSubscription,
  CollaborationPackageActivityFilter,
  CollaborationPackageFilter,
  CollaborationPackageItem,
  CollaborationPackageParams,
  CollaborationPackageStatusFilterNL,
  CollaborationPackageStatusFilterSE,
} from '../types/collaboration-package.types';

type BundleProductId = 'apppro' | 'appessential' | 'appgo' | 'appmini' | 'nllf001' | 'nllf002' | 'nllf003' | 'nllf004';

const isBundle = (module: AppModule) => module.type?.toUpperCase() === AppModuleType.BUNDLE;
const isMatchingBundleId = (subscription: AppSubscription | null, productId: BundleProductId) =>
  subscription?.modules?.find(isBundle)?.id === productId;

const allWithNewSubscription = (item: CollaborationPackageItem) =>
  item.subscription?.subscriptionTypeId === 4 && item.subscription?.modules?.length && !item.archived; // of new subscription type and not archived

const dutchSubscription = (item: CollaborationPackageItem) =>
  [5, 6].includes(item.subscription?.subscriptionTypeId) && item.subscription?.modules?.length && !item.archived; // of new subscription type and not archived

const filterStatusFunctionMapSE: Record<
  CollaborationPackageStatusFilterSE,
  (item: CollaborationPackageItem) => boolean
> = {
  all: () => true,
  allExceptArchived: (item: CollaborationPackageItem) => !item.archived,
  allWithoutSubscription: (item: CollaborationPackageItem) =>
    (!item.subscription?.subscriptionTypeId ||
      item.subscription?.subscriptionTypeId === 2 ||
      (item.subscription?.subscriptionTypeId === 4 && !item.subscription?.modules?.length)) &&
    !item.archived, // subscription type is 2 or subscription type is 4 and has no modules or not set and not archived
  allArchived: (item: CollaborationPackageItem) => item.archived,
  allWithFirmsupport: (item: CollaborationPackageItem) => item.existsInFirmSupport,
  allWithoutFirmsupport: (item: CollaborationPackageItem) => !item.existsInFirmSupport,

  allWithNewSubscription,
  allWithNewProSubscription: (item: CollaborationPackageItem) =>
    allWithNewSubscription(item) && isMatchingBundleId(item.subscription, 'apppro'), // of new subscription type and has pro module
  allWithNewEssentialSubscription: (item: CollaborationPackageItem) =>
    allWithNewSubscription(item) && isMatchingBundleId(item.subscription, 'appessential'), // of new subscription type and has essential module
  allWithNewGoSubscription: (item: CollaborationPackageItem) =>
    allWithNewSubscription(item) && isMatchingBundleId(item.subscription, 'appgo'), // of new subscription type and has go module
  allWithNewMiniSubscription: (item: CollaborationPackageItem) =>
    allWithNewSubscription(item) && isMatchingBundleId(item.subscription, 'appmini'), // of new subscription type and has mini module
};

const filterStatusFunctionMapNL: Record<
  CollaborationPackageStatusFilterNL,
  (item: CollaborationPackageItem) => boolean
> = {
  all: () => true,
  allWithFirmsupport: (item: CollaborationPackageItem) => item.existsInFirmSupport,
  allWithoutFirmsupport: (item: CollaborationPackageItem) => !item.existsInFirmSupport,

  allWithNewLargeSubscription: (item: CollaborationPackageItem) =>
    dutchSubscription(item) && isMatchingBundleId(item.subscription, 'nllf004'), // of new subscription type and has Large module
  allWithNewMediumSubscription: (item: CollaborationPackageItem) =>
    dutchSubscription(item) &&
    (isMatchingBundleId(item.subscription, 'nllf002') || isMatchingBundleId(item.subscription, 'nllf003')), // of new subscription type and has Medium module
  allWithNewSmallSubscription: (item: CollaborationPackageItem) =>
    dutchSubscription(item) && isMatchingBundleId(item.subscription, 'nllf001'), // of new subscription type and has Small module
};

const filterStatusFunctionMap = {
  [Country.SE]: { ...filterStatusFunctionMapSE },
  [Country.NL]: { ...filterStatusFunctionMapNL },
};

const getLastLogin = (item: CollaborationPackageItem): Date => (item.lastLogin ? new Date(item.lastLogin) : null);
const getWeekAgo = (): Date => {
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  return weekAgo;
};
const getMonthAgo = (): Date => {
  const monthAgo = new Date();
  monthAgo.setMonth(monthAgo.getMonth() - 1);
  return monthAgo;
};
const getYearAgo = (): Date => {
  const yearAgo = new Date();
  yearAgo.setFullYear(yearAgo.getFullYear() - 1);
  return yearAgo;
};

const filterActivityFunctionMap: Record<
  CollaborationPackageActivityFilter,
  (item: CollaborationPackageItem) => boolean
> = {
  all: () => true,
  noActivity: (item: CollaborationPackageItem) => {
    const lastLogin = getLastLogin(item);
    if (!lastLogin) {
      return true;
    }

    const yearAgo = getYearAgo();
    return lastLogin && lastLogin < yearAgo;
  },
  activityCurrentWeek: (item: CollaborationPackageItem) => {
    const lastLogin = getLastLogin(item);
    const weekAgo = getWeekAgo();
    return lastLogin && lastLogin >= weekAgo;
  },
  activityCurrentMonth: (item: CollaborationPackageItem) => {
    const lastLogin = getLastLogin(item);
    const monthAgo = getMonthAgo();
    return lastLogin && lastLogin >= monthAgo;
  },
  activityCurrentYear: (item: CollaborationPackageItem) => {
    const lastLogin = getLastLogin(item);
    const yearAgo = getYearAgo();
    return lastLogin && lastLogin >= yearAgo;
  },
};

const allCloudDbItems = ListSelectors.getItems<
  CollaborationPackageItem,
  CollaborationPackageFilter,
  CollaborationPackageParams
>();

const selectFilteredItems = createSelector(
  allCloudDbItems,
  FirmSelectors.selectCountryCode,
  ({ items, filter: { status, activity } }, countryCode) => {
    const filterStatusFunction = filterStatusFunctionMap[countryCode][status] || (() => true);
    const filterActivityFunction = filterActivityFunctionMap[activity] || (() => true);
    return items.filter(filterStatusFunction).filter(filterActivityFunction);
  },
);

const collaborationPackageItemState = (state: AppState) => state.collaborationPackageItem;

const selectEmail = createSelector(collaborationPackageItemState, (state) => state.email);
const selectClient = createSelector(collaborationPackageItemState, (state) => state.client);
const selectHasHiddenDatabases = createSelector(
  collaborationPackageItemState,
  (state) => state.meta?.hasHiddenDatabases ?? false,
);
const selectDeleteable = createSelector(collaborationPackageItemState, (state) => Boolean(state.client?.deleteable));
const selectNumberOfMissingCollaborationPackages = createSelector(
  collaborationPackageItemState,
  (state) => state.meta?.numberOfMissingCollaborationPackages ?? 0,
);

export const CollaborationPackageSelectors = {
  selectFilteredItems,
  selectEmail,
  selectClient,
  selectHasHiddenDatabases,
  selectDeleteable,
  selectNumberOfMissingCollaborationPackages,
};
